import React, { createContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

import { useUrlParams } from './UrlParamsHook';
import { getFecToken } from '../api/ControlPlaneAPI';

const SOCKET_IO_SERVER_URL = 'https://fec.feelme.com';
const SOCKET_IO_STAGING_URL = 'https://fec-stg.feelme.com';

let isSocketIOStaging = false;
let customSocketIOUrl = null;

export const setStagingSocketIO = (isStaging) => {
  isSocketIOStaging = isStaging;
};

export const setSocketIOUrl = (url) => {
  customSocketIOUrl = url;
};

const getSocketIOServerUrl = () => {
  if (!isSocketIOStaging) {
    return SOCKET_IO_SERVER_URL;
  }
  return customSocketIOUrl ? customSocketIOUrl : SOCKET_IO_STAGING_URL;
};

const SocketIOContext = createContext();

export const useSocketIO = () => {
  const context = React.useContext(SocketIOContext);
  if (context === undefined) {
    throw new Error('`useSocketIO` hook must be used within a `SocketIOContextProvider` component');
  }
  return context;
};

export const SocketIOContextProvider = ({ children }) => {
  const { roomNumber } = useUrlParams();

  const socketRef = useRef(null);
  const [isPartnerOnline, setIsPartnerOnline] = useState(false);
  const [cooldown, setCooldown] = useState(null);
  const [sessionMetadata, setSessionMetadata] = useState(null);

  useEffect(() => {
    if (!roomNumber) {
      return;
    }

    let cancelled = false;

    const connect = async () => {
      let fecToken;
      try {
        fecToken = await getFecToken(roomNumber);
      } catch (err) {
        console.warn('[SocketIO:Website] Failed to get FEC token:', err.message);
      }

      if (!fecToken || cancelled) {
        return;
      }

      const serverUrl = getSocketIOServerUrl();
      const socket = io(`${serverUrl}/pubnub`, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        auth: { token: fecToken },
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        // Join the webshare room
        socket.emit('room:join', { room_name: roomNumber }, (res) => {
          if (res && res.ok) {
            console.log('[SocketIO:Website] Joined room', roomNumber);
          } else {
            console.warn('[SocketIO:Website] Failed to join room', roomNumber, res && res.error);
          }
        });
      });

      socket.on('disconnect', (reason) => {
        console.log('[SocketIO:Website] Disconnected:', reason);
      });

      socket.on('connect_error', (err) => {
        console.warn('[SocketIO:Website] Connection error:', err.message);
      });

      socket.on('message', (payload) => {
        const { message_type, data } = payload;
        if (!message_type || !data) {
          return;
        }

        switch (message_type) {
          case 'webshare:cooldown':
            setCooldown(data.cooldown);
            break;
          case 'webshare:session_metadata':
            setSessionMetadata({
              sharingEndTime: data.sharingEndTime,
              username: data.username,
              devices: data.devices,
            });
            break;
          case 'system:presence':
            if (data.action === 'join' || data.action === 'not_empty') {
              setIsPartnerOnline(true);
            } else if (
              data.action === 'leave' ||
              data.action === 'empty' ||
              data.action === 'timeout'
            ) {
              setIsPartnerOnline(false);
            }
            break;
          default:
            break;
        }
      });
    };

    connect();

    return () => {
      cancelled = true;
      const socket = socketRef.current;
      if (socket) {
        socket.emit('room:leave', { room_name: roomNumber }, () => {});
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [roomNumber]);

  const sendKeonCommand = useCallback(
    (percent, position, isRangeMode) => {
      const socket = socketRef.current;
      if (!socket || !socket.connected || !roomNumber) {
        return false;
      }
      socket.emit(
        'message',
        {
          room: roomNumber,
          message_type: 'webshare:keon_command',
          data: {
            room: roomNumber,
            percent: Number(percent),
            position: typeof position === 'string' ? position : JSON.stringify(position),
            isRangeMode: !!isRangeMode,
          },
        },
        (res) => {
          if (res && !res.ok) {
            console.warn('[SocketIO:Website] keon_command failed', res.error);
          }
        },
      );
      return true;
    },
    [roomNumber],
  );

  const sendPowerShotCommand = useCallback(
    (firstMotorPercent, secondMotorPercent) => {
      const socket = socketRef.current;
      if (!socket || !socket.connected || !roomNumber) {
        return false;
      }
      socket.emit(
        'message',
        {
          room: roomNumber,
          message_type: 'webshare:keon_command',
          data: {
            room: roomNumber,
            firstMotorPercent: Number(firstMotorPercent),
            secondMotorPercent: Number(secondMotorPercent),
          },
        },
        (res) => {
          if (res && !res.ok) {
            console.warn('[SocketIO:Website] powershot_command failed', res.error);
          }
        },
      );
      return true;
    },
    [roomNumber],
  );

  const sendPowerblowCommand = useCallback(
    (customSettings) => {
      const socket = socketRef.current;
      if (!socket || !socket.connected || !roomNumber) {
        return false;
      }
      socket.emit(
        'message',
        {
          room: roomNumber,
          message_type: 'webshare:powerblow_command',
          data: {
            room: roomNumber,
            customSettings,
          },
        },
        (res) => {
          if (res && !res.ok) {
            console.warn('[SocketIO:Website] powerblow_command failed', res.error);
          }
        },
      );
      return true;
    },
    [roomNumber],
  );

  return (
    <SocketIOContext.Provider
      value={{
        sendKeonCommand,
        sendPowerblowCommand,
        sendPowerShotCommand,
        isPartnerOnline,
        cooldown,
        sessionMetadata,
      }}
    >
      {children}
    </SocketIOContext.Provider>
  );
};
